/* eslint-disable react/no-array-index-key */
import { PlusIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import OversizeRangeComponent from 'components/admin/oversize-range';
import FormInput from 'components/common/form-input';
import Button from 'components/inputs/button';
import { OversizeRangeType, SettingsType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { errorModal, successModal } from 'utils/ui';

type FilteringField = {
  code: keyof SettingsType;
  label: string;
  type: 'number';
  placeholder?: string;
  options?: string[];
};

export default function FilteringPage() {
  const dispatch = useAppDispatch();
  const { settings } = useAppSelector((state) => state.admin);
  const [values, setValues] = useState({});
  const [oversizeRanges, setOversizeRanges] = useState<OversizeRangeType[]>([]);

  const fields: FilteringField[] = [
    {
      code: 'coolingPrimaryUndersizePercent',
      label:
        'Cooling Primary Undersize % (lowest acceptable cooling-to-zone BTU ratio for primary cooling)',
      type: 'number',
      placeholder: '0%'
    },
    {
      code: 'heatingPrimaryUndersizePercent',
      label:
        'Heating Primary Undersize % (lowest acceptable heating-to-zone BTU ratio for primary heating)',
      type: 'number',
      placeholder: '0%'
    },
    {
      code: 'heatingSupplementalUndersizePercent',
      label:
        'Heating Supplemental Undersize % (lowest acceptable heating-to-zone BTU ratio for supplemental heating, measured against the full zone load)',
      type: 'number',
      placeholder: '0%'
    },
    {
      code: 'heatingSupplementalLoadPercent',
      label:
        'Heating Supplemental Load % (share of a supplemental zone heating load this system must carry)',
      type: 'number',
      placeholder: '0%'
    },
    {
      code: 'coolingSupplementalUndersizePercent',
      label:
        'Cooling Supplemental Undersize % (lowest acceptable cooling-to-zone BTU ratio for supplemental cooling, measured against the full zone load)',
      type: 'number',
      placeholder: '0%'
    },
    {
      code: 'coolingSupplementalLoadPercent',
      label:
        'Cooling Supplemental Load % (share of a supplemental zone cooling load this system must carry)',
      type: 'number',
      placeholder: '0%'
    }
  ];

  useEffect(() => {
    if (settings) {
      setValues(
        Object.fromEntries(
          fields.map((field) => [field.code, settings[field.code]]) as any
        )
      );
      setOversizeRanges(settings.oversizeBTURanges);
    }
  }, [settings]);

  const handleFieldChange = (code: string, value: any) => {
    setValues((oldValues) => ({ ...oldValues, [code]: value }));
  };

  const handleAddOversizeRange = () => {
    setOversizeRanges((oldRanges) => [
      ...oldRanges,
      { minBTU: undefined, maxBTU: undefined, oversizeBTUAmount: 0 }
    ]);
  };

  const handleSave = async () => {
    try {
      await axios.patch('settings', [
        ...Object.entries(values)
          .filter(([, value]) => value !== undefined && value !== null)
          .map(([key, value]) => ({ key, value: String(value) })),
        { key: 'oversizeBTURanges', value: JSON.stringify(oversizeRanges) }
      ]);
      dispatch(fetchDefaultAdminData());
      successModal(`Settings updated.`);
    } catch {
      errorModal('Something went wrong.');
    }
  };

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>
        System Filtering Settings
      </div>
      <table className='min-w-full divide-y divide-gray-300'>
        <thead>
          <tr>
            <th
              scope='col'
              className='pb-3.5 pl-0 pr-3 text-left font-semibold text-gray-900'
            >
              Setting
            </th>
            <th
              scope='col'
              className='px-3 pb-3.5 text-left font-semibold text-gray-900'
            >
              Description
            </th>
            <th
              scope='col'
              className='px-3 pb-3.5 text-left font-semibold text-gray-900'
            >
              Value
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200'>
          {fields.map((field) => (
            <tr key={field.code}>
              <td className='py-4 pl-0 pr-3 text-gray-900'>{field.code}</td>
              <td className='px-3 py-4 text-gray-500'>{field.label}</td>
              <td className='min-w-[200px] px-3 py-4'>
                <div className='inline-block'>
                  <FormInput
                    type={field.type}
                    value={values[field.code]}
                    onChange={(value) => handleFieldChange(field.code, value)}
                    completed={!!values[field.code]}
                    required
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className='flex flex-col gap-3'>
        <div className='font-semibold text-gray-900'>BTU Oversizing Ranges</div>
        {oversizeRanges.map((range, index) => (
          <OversizeRangeComponent
            key={index}
            range={range}
            index={index}
            updateArray={setOversizeRanges}
            deleteSelf={() =>
              setOversizeRanges((oldRanges) => {
                oldRanges.splice(index, 1);
                return [...oldRanges];
              })
            }
          />
        ))}
        <div className='inline-block'>
          <Button
            text='Add Oversizing Range'
            color='secondary'
            icon={PlusIcon}
            onClick={handleAddOversizeRange}
          />
        </div>
      </div>
      <div>
        <Button text='Save' color='green' onClick={handleSave} />
      </div>
    </div>
  );
}
